import { FormGroup } from '@angular/forms';
import type { FormField } from '../../interfaces/form.interface';
import { InputType } from '../../interfaces/input.interface';
import { DropdownSelectedValueService } from './dropdown-selected-value.service';
import { FormOperationState } from './form-operation-state';

/** Executes calculated field values defined through FormField.updateValue. */
export class FieldUpdateExecutor<T> {
    constructor(
        private readonly model: FormGroup,
        private readonly state: FormOperationState,
        private readonly selectedValueService: DropdownSelectedValueService<T>,
    ) {}

    public applyUpdateValue(field: FormField<T>): void {
        if (!field.updateValue || this.state.isProgrammaticChange) {
            return;
        }

        const value = field.updateValue(field);

        if (value === undefined) {
            return;
        }

        const controlName = field.name ?? '';
        const control = this.model.get(controlName);
        const nextValue = this.normalizeValue(field, value);

        this.selectedValueService.applySelected(field, nextValue);

        if (!control || this.areValuesEquivalent(control.value, nextValue)) {
            return;
        }

        this.state.runProgrammaticChange(() => {
            this.model.patchValue({ [controlName]: nextValue }, { emitEvent: false });
            control.markAsPristine();
            control.markAsUntouched();
        });
    }

    private normalizeValue(field: FormField<T>, value: T | string | null): T | string | null | Array<T | string | null> {
        if (this.isMultiSelect(field) && !Array.isArray(value)) {
            return [value];
        }

        return value;
    }

    private areValuesEquivalent(currentValue: unknown, nextValue: unknown): boolean {
        return this.toComparableValue(currentValue) === this.toComparableValue(nextValue);
    }

    private toComparableValue(value: unknown): string {
        if (Array.isArray(value)) {
            return JSON.stringify(value.map(item => this.toComparableValue(item)));
        }

        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { id?: unknown }).id ?? '');
        }

        if (value === null || value === undefined) {
            return '';
        }

        return String(value);
    }

    private isMultiSelect(field: FormField<T>): boolean {
        return field.input?.type === InputType.MULTISELECT_SEARCH || field.input?.type === InputType.MULTISELECT;
    }
}
